import { cloneDeep, concat, omit, uniqBy } from 'lodash';
import { reaction } from 'mobx';
import { useObserver } from 'mobx-react';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FB, FBMPIProcedureItemProps, FBMPIProcedureProps, FBMPIProcedureState, FBProcedureItemType, FBWorkspaceModeOptions } from '..';
import { companySelectors } from '../../../state/ducks/company';
import { documentRevisionsActions } from '../../../state/ducks/documentRevisions';
import { DOC_TYPE_GROUP } from '../../../state/ducks/documentRevisions/documentType/types';
import { SmartReference, SmartReferenceType } from '../../../state/ducks/documentRevisions/types';
import { checkIsDocumentForm, checkIsDocumentMPI } from '../../documentRevision/helpers/checkDocumentGroup';
import FBAutocompleteAsyncStore from '../FBAutocompleteAsync/FBAutocompleteAsync.store';
import { FBMPIProcedureSelectValue } from '../FBMPIProcedureSelect/FBMPIProcedureSelect.types';
import { observeMPIProcedure } from './FBMPIProcedure.reactions';

export const withFBMPIProcedure = <T extends FBMPIProcedureProps>(
  Component: React.FC<T>,
): React.FC<T> => {
  const Comp = ({
    handleAdd,
    onKeyDown,
    mpiProcedureState,
    name = '',
    isInputOwner,
    isOutput,
    isPreview,
    disabled,
    shouldSkipSerialization,
    ...props
  }: T) => {
    const isFieldDisabled = disabled;

    const { workspaceState, formState, serialsState } = FB.useStores();
    const mode = useObserver(() => workspaceState?.mode);
    const groupOptions = workspaceState?.document?.document.documentType.groupOptions;
    const isDocumentForm = checkIsDocumentForm(groupOptions);
    const isDocumentMPI = checkIsDocumentMPI(groupOptions);
    const isDesign = mode === FBWorkspaceModeOptions.DESIGN;
    const revisionId = workspaceState?.id;
    const dispatch = useDispatch();
    const redlineActive = useSelector(companySelectors.getRedlineActive);

    mpiProcedureState = FB.useRef(FBMPIProcedureState, {
      ...props.editorConfig,
    });

    isOutput = Boolean(workspaceState?.isOutput && workspaceState?.id);
    isPreview = (isOutput || ['preview', 'formPreview'].includes(mode ?? '')) && !redlineActive;
    const isItemPreview = isPreview && mode !== 'formPreview';
    // Use the document form flag for MPI (ENC-7319).
    const isSelectVisible = (!isDocumentForm && mode !== 'preview' && mode !== 'formPreview')
      || (props.redlineActive ?? false);

    isInputOwner = workspaceState?.getIsInputOwner(name);
    disabled = (!isInputOwner || isPreview) && !redlineActive;

    const handleSelectionDelete = useCallback((refId?: string) => {
      const documentId = workspaceState?.document?.id;
      if (!refId || !documentId) { return; }
      mpiProcedureState?.selectionDelete(documentId, refId);
    }, [workspaceState, mpiProcedureState]);

    const sectionCallbacks = useMemo(() => new Map(Object.values(FBProcedureItemType).map(
      (type): [FBProcedureItemType, Pick<FBMPIProcedureItemProps, 'handleRemove' | 'getItem'>] => [type, {
        handleRemove: (id: string) => mpiProcedureState?.removeItem(id, type),
        getItem: (id: string) => mpiProcedureState?.getItem(id, type) ?? '',
      }],
    )), [mpiProcedureState]);

    const availableTypes = useMemo(() => {
      return Object.values(FBProcedureItemType).filter((type) => type !== FBProcedureItemType.serials || !shouldSkipSerialization);
    }, [shouldSkipSerialization]);

    useEffect(() => {
      if (isDocumentMPI || shouldSkipSerialization) { return; }
      serialsState?.load();
    }, [serialsState, isDocumentMPI, shouldSkipSerialization]);

    const items = useObserver(() => availableTypes.filter((type) => {
      const attachments = mpiProcedureState?.value?.attachments;
      switch (type) {
        case FBProcedureItemType.attachments:
          return !isPreview || (attachments?.length ?? 0) > 0;
        case FBProcedureItemType.serials:
          return isDocumentMPI || serialsState?.isPartSerialized;
        default: {
          const itemValues = mpiProcedureState?.[type] ?? [];
          return !isPreview || itemValues.length > 0;
        }
      }
    }).map((type): FBMPIProcedureItemProps => ({
      type,
      name,
      stepIndex: props.stepIndex ?? 0,
      disabled,
      isFieldDisabled,
      isPreview: isItemPreview,
      isSelectVisible,
      redlineActive,
      isDesign,
      isDocumentMPI,
      defaultValue: type === FBProcedureItemType.attachments ? props.editorConfig?.attachments : undefined,
      value: type === FBProcedureItemType.materials || type === FBProcedureItemType.equipment
        ? mpiProcedureState?.[type]
        : undefined,
      handleSelectionDelete,
      ...sectionCallbacks.get(type),
    })));

    const processedTypes = useObserver(() => items.reduce<string[]>((ready, { type }) => {
      switch (type) {
        case FBProcedureItemType.equipment:
          return (
            mpiProcedureState?.equipmentId
            && FBAutocompleteAsyncStore.data.get(mpiProcedureState?.equipmentId)
              ? [...ready, type]
              : ready
          );
        case FBProcedureItemType.materials:
          return (
            mpiProcedureState?.materialsId
            && mpiProcedureState?.lotsId
            && FBAutocompleteAsyncStore.data.get(mpiProcedureState?.materialsId)
            && FBAutocompleteAsyncStore.data.get(mpiProcedureState?.lotsId)
              ? [...ready, type]
              : ready
          );
        case FBProcedureItemType.serials:
          return isDocumentMPI || !serialsState?.isLoading ? [...ready, type] : ready;
        default:
          return [...ready, type];
      }
    }, []));

    // Selections belong to the step and must be ready before deferred items mount.
    useEffect(() => {
      if (!mpiProcedureState) { return; }
      const procedureState = mpiProcedureState;
      const document = workspaceState?.document;
      const isDocPIInstance = document?.document.documentType.group === DOC_TYPE_GROUP.PI_INSTANCE;

      const setFilteredParts = (references: SmartReference[] = []) => {
        const partsSelected = references.reduce<Array<Partial<FBMPIProcedureSelectValue>>>((selected, ref) => {
          if (ref?.metadata?.fieldId === name
            && ref.active
            && (ref.type === SmartReferenceType.EquipmentReplacement || ref.type === SmartReferenceType.LotUsage)) {
            const metadata: Partial<FBMPIProcedureSelectValue> = ref.metadata;
            selected.push({
              ...metadata,
              refId: ref.id,
              overrideDocId: ref.toDocRev?.document.docId,
              pmDueDate: ref.toDocRev?.precalc?.pm_due_date,
              calDueDate: ref.toDocRev?.precalc?.cali_due_date,
              lotPartRevision: ref.toDocRev?.precalc?.lot_part_revision,
              lotId: ref.toDocRevId ?? undefined,
              eqStatus: metadata.eqStatus ?? ref.toDocRev?.eqStatus,
              actualId: ref.type === SmartReferenceType.LotUsage ? metadata.actualId : ref.toDocRevId ?? undefined,
            });
          }
          return selected;
        }, []);
        procedureState.setPartsSelected(partsSelected);
      };

      const smartReferences = isDocPIInstance
        ? document?.smartReferencesTo
        : uniqBy(concat(document?.document.smartReferencesTo ?? [], document?.smartReferencesTo ?? []), 'id');
      setFilteredParts(smartReferences?.filter(ref => (ref?.fromDocRev?.version ?? 1) <= (document?.version ?? 1)));

      return reaction(
        () => procedureState.deleteLotApi.data,
        (data) => {
          if (!data || workspaceState?.id !== revisionId) { return; }
          const refs = isDocPIInstance
            ? data.smartReferencesTo
            : uniqBy(concat(data.smartReferencesTo ?? [], data.document.smartReferencesTo ?? []), 'id');
          setFilteredParts(refs);
          revisionId && dispatch(documentRevisionsActions.reload(revisionId));
        },
      );
    }, [name, mpiProcedureState, workspaceState, revisionId, dispatch]);

    useEffect(() => {
      if (!mpiProcedureState) { return; }
      return observeMPIProcedure(name, mpiProcedureState, workspaceState, formState);
    }, [name, mpiProcedureState, workspaceState, formState]);

    handleAdd = useCallback((isClone?: boolean, isFromKeys?: boolean) => {
      if (!workspaceState) { return; }
      const newName = FB.uniqid;
      const currentIndex = workspaceState.schema?.findIndex(item => item.name === name && !item.deleted) ?? -1;
      let schemaItem = workspaceState.getSchemaItemAt(currentIndex);
      if (!schemaItem) { return; }
      if (!isClone) {
        schemaItem = omit(schemaItem, 'editorConfig');
      }
      const newItem = {
        ...cloneDeep(schemaItem),
        autoScrollTo: isFromKeys,
        autoFocus: isFromKeys,
        name: newName,
      };
      formState?.initializeMPIValues([newItem]);
      workspaceState.setSchemaItemAt(newItem, currentIndex + 1);
      formState?.flushChangeValidation();
      workspaceState.saveDocRev(formState?.getValues());
    }, [workspaceState, formState, name]);

    onKeyDown = useCallback((e: KeyboardEvent) => {
      if (e.key === 'Enter' && e.ctrlKey) {
        handleAdd?.(false, true);
      }
    }, [handleAdd]);

    return Component({
      ...(props as T),
      handleAdd,
      onKeyDown,
      mpiProcedureState,
      isPreview,
      name,
      disabled,
      items,
      processedTypes,
      isFieldDisabled,
      shouldSkipSerialization,
    });
  };

  Comp.displayName = 'withFBMPIProcedure';
  return Comp;
};
